import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { CheckIn } from '@ultisim/shared';
import { MOOD_EMOJIS, MOOD_LABELS } from '@ultisim/shared';
import { format, startOfWeek, endOfWeek } from 'date-fns';

export default function CheckInPage() {
  const [checkin, setCheckin] = useState<CheckIn | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const [mood, setMood] = useState<number | null>(null);
  const [wins, setWins] = useState('');
  const [challenges, setChallenges] = useState('');
  const [helpNeeded, setHelpNeeded] = useState('');

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  useEffect(() => {
    const loadCheckin = async () => {
      try {
        const dateStr = format(now, 'yyyy-MM-dd');
        const result = await api.get<CheckIn | null>('/checkins/week', { date: dateStr });
        if (result) {
          setCheckin(result);
          setMood(result.mood);
          setWins(result.wins || '');
          setChallenges(result.challenges || '');
          setHelpNeeded(result.helpNeeded || '');
        }
      } catch (err) {
        console.error('Failed to load check-in:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadCheckin();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const saveCheckin = async () => {
    setIsSaving(true);
    try {
      const result = await api.post<CheckIn>('/checkins', {
        mood,
        wins: wins || undefined,
        challenges: challenges || undefined,
        helpNeeded: helpNeeded || undefined,
      });
      setCheckin(result);
      setSuccessMsg('Check-in saved!');
    } catch (err) {
      console.error('Failed to save check-in:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Weekly Check-in</h1>
        <p className="text-dark-400 mt-1">
          {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
        </p>
      </div>

      {successMsg && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
          {successMsg}
        </div>
      )}

      {/* Mood selector */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">How are you feeling this week?</h2>
        <div className="flex justify-center gap-4">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              onClick={() => setMood(value)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                mood === value
                  ? 'bg-primary-500/20 border-2 border-primary-500 scale-110'
                  : 'bg-dark-800 border-2 border-transparent hover:border-dark-600'
              }`}
            >
              <span className="text-3xl">{MOOD_EMOJIS[value]}</span>
              <span className={`text-xs ${mood === value ? 'text-primary-400' : 'text-dark-400'}`}>
                {MOOD_LABELS[value]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Wins */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-2">What were your wins this week? 🎉</h2>
        <p className="text-dark-500 text-sm mb-3">What went well? What are you proud of?</p>
        <textarea
          value={wins}
          onChange={(e) => setWins(e.target.value)}
          placeholder="I completed the new API integration, helped onboard a new team member..."
          className="input w-full h-24 resize-none"
        />
      </div>

      {/* Challenges */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-2">Any challenges? 🤔</h2>
        <p className="text-dark-500 text-sm mb-3">What obstacles did you face? What slowed you down?</p>
        <textarea
          value={challenges}
          onChange={(e) => setChallenges(e.target.value)}
          placeholder="Blocked on the database migration, need access to staging environment..."
          className="input w-full h-24 resize-none"
        />
      </div>

      {/* Help needed */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-2">What do you need help with? 🤝</h2>
        <p className="text-dark-500 text-sm mb-3">Anything your manager or teammates can help with?</p>
        <textarea
          value={helpNeeded}
          onChange={(e) => setHelpNeeded(e.target.value)}
          placeholder="Could use a code review on the PR, need clarification on requirements..."
          className="input w-full h-24 resize-none"
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button onClick={saveCheckin} disabled={isSaving} className="btn-primary">
          {isSaving ? (
            <><span className="spinner w-4 h-4" /> Saving...</>
          ) : checkin ? (
            'Update Check-in'
          ) : (
            'Submit Check-in'
          )}
        </button>
      </div>
    </div>
  );
}
